package data.objects

import apiclient.FormationClient
import apiclient.customfields.FieldValue
import apiclient.geoobjects.AddKeywords
import apiclient.geoobjects.GeoObjectDetails
import apiclient.geoobjects.ObjectChanges
import apiclient.geoobjects.ObjectType
import apiclient.geoobjects.RemoveField
import apiclient.geoobjects.RemoveKeywords
import apiclient.geoobjects.applyObjectChanges
import apiclient.validations.parseEnumValue
import dev.fritz2.routing.MapRouter
import koin.koinCtx
import koin.withKoin
import mainmenu.RouterStore
import mapsearch.updateInCaches
import model.KeywordTag
import model.L
import overlays.BusyStore
import search.global.ActiveSearchComponentNavStore
import search.global.ActiveSearchKeywordsStore
import search.global.ActiveSearchObjectTypesStore
import search.global.ActiveSearchReadOnlyKeywordsStore
import search.global.SearchComponentNav
import search.nestedObjects.ActiveNestedObjectTypesStore
import search.nestedObjects.ActiveNestedObjectsReadOnlyKeywordsStore
import search.nestedObjects.ActiveNestedSearchKeywordStore
import search.searchFilterStores.ActiveKeywordsStore

class ActiveObjectKeywordsStore : ActiveKeywordsStore() {

    private val activeSearchKeyWordsStore: ActiveSearchKeywordsStore by koinCtx.inject()
    private val activeNestedSearchKeywordStore: ActiveNestedSearchKeywordStore by koinCtx.inject()
    private val activeSearchObjectTypesStore: ActiveSearchObjectTypesStore by koinCtx.inject()
    private val activeNestedObjectTypesStore: ActiveNestedObjectTypesStore by koinCtx.inject()
    private val activeSearchReadOnlyKeywordsStore: ActiveSearchReadOnlyKeywordsStore by koinCtx.inject()
    private val activeNestedObjectsReadOnlyKeywordsStore: ActiveNestedObjectsReadOnlyKeywordsStore by koinCtx.inject()
    val router: MapRouter by koinCtx.inject()
    val routerStore: RouterStore by koinCtx.inject()
    val activeObjectStore: ActiveObjectStore by koinCtx.inject()
    val keywordsSub = activeObjectStore.map(GeoObjectDetails.L.keywords)
    private val activeSearchComponentNavStore: ActiveSearchComponentNavStore by koinCtx.inject()
    private val busyStore: BusyStore by koinCtx.inject()
    private val client: FormationClient by koinCtx.inject()


    val addToActiveSearchKeywords = handle<String> { current, keyword ->
        activeSearchKeyWordsStore.add(keyword)
        activeSearchComponentNavStore.update(SearchComponentNav.Search)
        current
    }

    val addToActiveNestedObjectKeywords = handle<String> { current, keyword ->
        activeNestedSearchKeywordStore.add(keyword)
        current
    }

    val addToActiveSearchObjectTypes = handle<String?> { current, objTypeString ->
        parseEnumValue<ObjectType>(objTypeString)?.let { activeSearchObjectTypesStore.add(it) }
        activeSearchComponentNavStore.update(SearchComponentNav.Search)
        current
    }
    val addToActiveSearchObjectTypesWithoutRouting = handle<String?> { current, objTypeString ->
        parseEnumValue<ObjectType>(objTypeString)?.let { activeSearchObjectTypesStore.add(it) }
        current
    }

    val removeFromActiveSearchObjectTypes = handle<String?> { current, objTypeString ->
        parseEnumValue<ObjectType>(objTypeString)?.let { activeSearchObjectTypesStore.remove(it) }
        current
    }

    val addToActiveNestedObjectTypesWithoutRouting = handle<String?> { current, objTypeString ->
        parseEnumValue<ObjectType>(objTypeString)?.let { activeNestedObjectTypesStore.add(it) }
        current
    }

    val removeFromActiveNestedObjectTypes = handle<String?> { current, objTypeString ->
        parseEnumValue<ObjectType>(objTypeString)?.let { activeNestedObjectTypesStore.remove(it) }
        current
    }

    val addToActiveSearchReadOnlyTags = handle<KeywordTag> { current, otherTag ->
        activeSearchReadOnlyKeywordsStore.add(otherTag)
        activeSearchComponentNavStore.update(SearchComponentNav.Search)
        current
    }

    val removeFromActiveSearchReadOnlyTags = handle<KeywordTag> { current, otherTag ->
        activeSearchReadOnlyKeywordsStore.remove(otherTag)
        current
    }

    val addToActiveNestedObjectReadOnlyTags = handle<KeywordTag> { current, otherTag ->
        activeNestedObjectsReadOnlyKeywordsStore.add(otherTag)
        current
    }

    val removeFromActiveNestedObjectReadOnlyTags = handle<KeywordTag> { current, otherTag ->
        activeNestedObjectsReadOnlyKeywordsStore.remove(otherTag)
        current
    }

    val directEditAddKeywordToActiveObject = handle<String> { current, keyword ->
        busyStore.handleApiCall(
            supplier = {
                client.applyObjectChanges(ObjectChanges(activeObjectStore.current.id, AddKeywords(listOf(keyword))))
            },
            processResult = { geoObjects ->
                val updatedActiveObj = geoObjects.firstOrNull { it.id == activeObjectStore.current.id }
                updatedActiveObj?.let { updated ->
                    keywordsSub.update(updated.keywords)
                    updateInCaches(updated)
                }
            },
            withBusyState = true,
        )
        current
    }

    val directEditRemoveKeywordFromActiveObject = handle<String> { current, keyword ->
        busyStore.handleApiCall(
            supplier = {
                client.applyObjectChanges(ObjectChanges(activeObjectStore.current.id, RemoveKeywords(listOf(keyword))))
            },
            processResult = { geoObjects ->
                val updatedActiveObj = geoObjects.firstOrNull { it.id == activeObjectStore.current.id }
                updatedActiveObj?.let { updated ->
                    keywordsSub.update(updated.keywords)
                }
            },
            withBusyState = true,
        )
        current
    }

    val directEditRemoveFieldValueFromObject = handle<FieldValue?> { current, fieldValue ->
        if (fieldValue != null) {
            withKoin {
                val busyStore: BusyStore = get()
                val client: FormationClient = get()
                val activeObjectStore: ActiveObjectStore = get()
                val tagsSub = activeObjectStore.map(GeoObjectDetails.L.tags)

                busyStore.handleApiCall(
                    supplier = {
                        client.applyObjectChanges(
                            ObjectChanges(
                                activeObjectStore.current.id,
                                RemoveField(listOf(fieldValue.field)),
                            ),
                        )
                    },
                    processResult = { geoObjects ->
                        val updatedActiveObj = geoObjects.firstOrNull { it.id == activeObjectStore.current.id }
                        updatedActiveObj?.let { updated ->
                            // update activeObject with new fieldValues
                            tagsSub.update(updated.tags)
                        }
                    },
                    withBusyState = true,
                )
            }
        }
        current
    }
}
